import React from 'react';
import { Check, AlertTriangle, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

/**
 * FieldValue - Displays a field value with comparison to DB value
 * Shows match/mismatch status with visual indicators and tooltips
 */
export const FieldValue = ({ value, dbValue, isMatch, isOriginalRow = false }) => {
  const formatAmount = (val) => {
    if (val === null || val === undefined || val === '') return null;
    return typeof val === 'number' ? `$${val.toFixed(2)}` : val;
  };

  // Handle legacy format where dbValue is an object {csv_value, db_value, match, corrected_value}
  let actualDbValue = dbValue;
  let actualMatch = isMatch;
  let actualCsvValue = value;
  
  if (dbValue && typeof dbValue === 'object' && 'db_value' in dbValue) {
    actualDbValue = dbValue.db_value;
    actualMatch = dbValue.match;
    actualCsvValue = dbValue.csv_value;
  }

  const formattedCsv = formatAmount(actualCsvValue);
  const formattedDb = formatAmount(actualDbValue);

  if (isOriginalRow) {
    return <span className="text-muted-foreground">{formattedCsv || '-'}</span>;
  }

  // No DB value to compare against — show raw value
  if (actualDbValue === undefined || actualDbValue === null) {
    return <span>{formattedCsv || '-'}</span>;
  }

  // CSV value missing but DB value exists — show DB value with info icon
  if (formattedCsv === null && formattedDb !== null) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400">
              <Info className="h-3 w-3" />
              {formattedDb}
            </span>
          </TooltipTrigger>
          <TooltipContent><p>From DB (not in CSV)</p></TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Check if values are effectively the same (handles float precision and formatting)
  const valuesEffectivelySame = (() => {
    if (actualMatch) return true;
    if (formattedCsv === formattedDb) return true;
    const csvNum = parseFloat(actualCsvValue);
    const dbNum = parseFloat(actualDbValue);
    if (!isNaN(csvNum) && !isNaN(dbNum) && Math.abs(csvNum - dbNum) < 0.005) return true;
    return false;
  })();

  if (valuesEffectivelySame) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
              <Check className="h-3 w-3" />
              {formattedDb}
            </span>
          </TooltipTrigger>
          <TooltipContent><p>Matches DB</p></TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Values differ — show correction with tooltip
  const csvNum = parseFloat(actualCsvValue);
  const dbNum = parseFloat(actualDbValue);
  const diff = (!isNaN(csvNum) && !isNaN(dbNum)) ? (dbNum - csvNum) : null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30">
            <AlertTriangle className="h-3 w-3 text-amber-600" />
            <span className="line-through text-amber-600 text-xs">{formattedCsv || '-'}</span>
            <span className="font-medium text-amber-800 dark:text-amber-200">{formattedDb}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">Corrected by validation</p>
          <p>CSV: {formattedCsv || 'N/A'} → DB: {formattedDb}</p>
          {diff !== null && (
            <p className="text-xs mt-1">
              Difference: {diff >= 0 ? '+' : ''}{`$${diff.toFixed(2)}`}
              {Math.abs(diff) < 5 && ' (likely late fee)'}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FieldValue;
